type LogLevel = 'info' | 'warn' | 'error';
type GTMEvent = 'page_view' | 'scroll' | string;

interface LogMessage {
  level: LogLevel;
  message: string;
  details?: unknown;
  timestamp: string;
}

interface GTMEventData {
  event: GTMEvent;
  [key: string]: unknown;
}

const logs: LogMessage[] = [];
const eventCache = new Map<string, number>();
const DEBOUNCE_TIME = 2000; // 2 seconds

export const logger = {
  info: (message: string, details?: unknown) => {
    const log = createLog('info', message, details);
    logs.push(log);
    if (import.meta.env.DEV) {
      console.warn(`[INFO ${log.timestamp}] ${message}`, details || '');
    }
  },
  
  warn: (message: string, details?: unknown) => {
    const log = createLog('warn', message, details);
    logs.push(log);
    if (import.meta.env.DEV) {
      console.warn(`[${log.timestamp}] ${message}`, details || '');
    }
  },
  
  error: (message: string, details?: unknown) => {
    const log = createLog('error', message, details);
    logs.push(log);
    if (import.meta.env.DEV) {
      console.error(`[${log.timestamp}] ${message}`, details || '');
    }
  },

  gtm: {
    pushEvent: (eventName: GTMEvent, eventData: Record<string, unknown> = {}) => {
      const now = Date.now();
      const lastEvent = eventCache.get(eventName);
      
      if (lastEvent && (now - lastEvent) < DEBOUNCE_TIME) {
        logger.info(`GTM event '${eventName}' debounced`);
        return;
      }
      
      eventCache.set(eventName, now);
      
      const event: GTMEventData = {
        event: eventName,
        ...eventData,
        timestamp: new Date().toISOString()
      };

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(event);
      
      logger.info(`GTM event pushed: ${eventName}`, event);
    }
  },

  getLogs: () => [...logs]
};

function createLog(level: LogLevel, message: string, details?: unknown): LogMessage {
  return {
    level,
    message,
    details,
    timestamp: new Date().toISOString()
  };
}